import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Chess } from 'chess.js';
import toast from 'react-hot-toast';
import { db } from '../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { playSound, PIECE_SETS, BOARD_THEMES } from '../components/Board/BoardConstants';

export default function BoardTools({ user, setActiveTab }) {
  const [boardTheme, setBoardTheme] = useState('green');
  const [pieceTheme, setPieceTheme] = useState('cburnett');
  
  // Tab state: 'analysis' or 'editor'
  const [activeSubTab, setActiveSubTab] = useState('analysis');

  // Sync user theme preferences
  useEffect(() => {
    if (!user?.email) return;
    const unsub = onSnapshot(doc(db, 'users', user.email.toLowerCase()), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        if (d.boardTheme) setBoardTheme(d.boardTheme);
        if (d.pieceTheme) setPieceTheme(d.pieceTheme);
      }
    });
    return () => unsub();
  }, [user]);

  const { light: lightSquareColor, dark: darkSquareColor } = BOARD_THEMES[boardTheme] || BOARD_THEMES.green;
  const currentPieceSet = PIECE_SETS[pieceTheme] || PIECE_SETS.cburnett;

  // -------------------------------------------------------------
  // STATE & INITIALIZATION FOR: ANALYSIS TAB
  // -------------------------------------------------------------
  const [analysisGame] = useState(new Chess());
  const [analysisHistory, setAnalysisHistory] = useState([analysisGame.fen()]);
  const [analysisMoveHistory, setAnalysisMoveHistory] = useState([]); // array of SAN moves
  const [analysisViewIndex, setAnalysisViewIndex] = useState(0);
  const [analysisSelectedSq, setAnalysisSelectedSq] = useState(null);
  const [analysisLegalMoves, setAnalysisLegalMoves] = useState([]);
  const [analysisIsFlipped, setAnalysisIsFlipped] = useState(false);
  const [analysisFenInput, setAnalysisFenInput] = useState('');
  const [analysisPgnInput, setAnalysisPgnInput] = useState('');

  // Sizing hook to keep board square and responsive
  const [boardWidth, setBoardWidth] = useState(420);
  useEffect(() => {
    const handleResize = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const paddingAndPanels = vw <= 768 ? 40 : 540;
      let optimalWidth = Math.min(vw - paddingAndPanels, vh - 220);
      optimalWidth = Math.max(280, Math.min(480, optimalWidth));
      setBoardWidth(optimalWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentAnalysisFen = analysisHistory[analysisViewIndex];

  // Build piece mapping of coordinates -> piece codes (e.g. 'e4' -> 'wP')
  const analysisPieceMap = useMemo(() => {
    const map = {};
    try {
      const temp = new Chess(currentAnalysisFen);
      const rows = ['8','7','6','5','4','3','2','1'];
      const cols = ['a','b','c','d','e','f','g','h'];
      temp.board().forEach((row, rIdx) => row.forEach((sq, cIdx) => {
        if (sq) map[`${cols[cIdx]}${rows[rIdx]}`] = sq.color + sq.type.toUpperCase();
      }));
    } catch(e){}
    return map;
  }, [currentAnalysisFen]);

  // Pointer Drag states for Analysis Board
  const [analysisDragSq, setAnalysisDragSq] = useState(null);
  const [analysisDragPos, setAnalysisDragPos] = useState(null);
  const analysisBoardRef = useRef(null);
  const analysisIsDraggingRef = useRef(false);
  const analysisPointerDownPosRef = useRef(null);
  const analysisPointerDownSelectionRef = useRef(null);
  const analysisPointerDownOffsetRef = useRef({ x: 0, y: 0 });
  const analysisJustDraggedRef = useRef(false);

  const sqSize = boardWidth / 8;
  const baseRows = ['8','7','6','5','4','3','2','1'];
  const baseCols = ['a','b','c','d','e','f','g','h'];
  const analysisRows = analysisIsFlipped ? [...baseRows].reverse() : baseRows;
  const analysisCols = analysisIsFlipped ? [...baseCols].reverse() : baseCols;

  const pixelToSquareAnalysis = (clientX, clientY) => {
    if (!analysisBoardRef.current) return null;
    const rect = analysisBoardRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (x < 0 || y < 0 || x > boardWidth || y > boardWidth) return null;
    const cIdx = Math.floor(x / sqSize);
    const rIdx = Math.floor(y / sqSize);
    if (cIdx < 0 || cIdx > 7 || rIdx < 0 || rIdx > 7) return null;
    return `${analysisCols[cIdx]}${analysisRows[rIdx]}`;
  };

  const handlePointerDownAnalysis = (e, id) => {
    const temp = new Chess(currentAnalysisFen);
    const piece = temp.get(id);
    if (!piece) return;

    analysisPointerDownSelectionRef.current = analysisSelectedSq;
    analysisIsDraggingRef.current = false;
    analysisPointerDownPosRef.current = { x: e.clientX, y: e.clientY };

    const rect = analysisBoardRef.current.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;

    const cIdx = analysisCols.indexOf(id[0]);
    const rIdx = analysisRows.indexOf(id[1]);
    const pieceLeft = cIdx * sqSize;
    const pieceTop = rIdx * sqSize;
    analysisPointerDownOffsetRef.current = { x: cursorX - pieceLeft, y: cursorY - pieceTop };

    setAnalysisDragSq(id);
    setAnalysisDragPos({ x: cursorX, y: cursorY });

    if (analysisSelectedSq !== id) {
      setAnalysisSelectedSq(id);
      setAnalysisLegalMoves(temp.moves({ square: id, verbose: true }).map(m => m.to));
    }
  };

  const executeMoveAnalysis = (from, to) => {
    try {
      const activeFen = analysisHistory[analysisViewIndex];
      const temp = new Chess(activeFen);
      
      // Attempt moving piece with auto-promotion to queen
      const move = temp.move({ from, to, promotion: 'q' });
      if (move) {
        playSound(move.captured ? 'capture' : 'move');
        
        // Truncate history forward if we were viewing a past move (branching)
        const slicedHistory = analysisHistory.slice(0, analysisViewIndex + 1);
        const slicedMoveHistory = analysisMoveHistory.slice(0, analysisViewIndex);

        const newFen = temp.fen();
        const nextHistory = [...slicedHistory, newFen];
        const nextMoves = [...slicedMoveHistory, move.san];

        setAnalysisHistory(nextHistory);
        setAnalysisMoveHistory(nextMoves);
        setAnalysisViewIndex(nextHistory.length - 1);
        setAnalysisSelectedSq(null);
        setAnalysisLegalMoves([]);
      }
    } catch(e) {
      playSound('error');
    }
  };

  useEffect(() => {
    if (!analysisDragSq) return;

    const handleGlobalPointerMove = (e) => {
      if (!analysisPointerDownPosRef.current) return;
      const dx = e.clientX - analysisPointerDownPosRef.current.x;
      const dy = e.clientY - analysisPointerDownPosRef.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        analysisIsDraggingRef.current = true;
      }
      if (analysisBoardRef.current) {
        const rect = analysisBoardRef.current.getBoundingClientRect();
        setAnalysisDragPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }
    };

    const handleGlobalPointerUp = (e) => {
      const targetSq = pixelToSquareAnalysis(e.clientX, e.clientY);
      const wasDragMove = analysisIsDraggingRef.current && targetSq && targetSq !== analysisDragSq;
      const draggedId = analysisDragSq;

      if (analysisIsDraggingRef.current) {
        analysisJustDraggedRef.current = true;
        setTimeout(() => { analysisJustDraggedRef.current = false; }, 50);
      }

      setAnalysisDragSq(null);
      setAnalysisDragPos(null);
      analysisPointerDownPosRef.current = null;

      if (wasDragMove && analysisLegalMoves.includes(targetSq)) {
        executeMoveAnalysis(draggedId, targetSq);
      }
    };

    window.addEventListener('pointermove', handleGlobalPointerMove);
    window.addEventListener('pointerup', handleGlobalPointerUp);
    return () => {
      window.removeEventListener('pointermove', handleGlobalPointerMove);
      window.removeEventListener('pointerup', handleGlobalPointerUp);
    };
  }, [analysisDragSq, analysisLegalMoves, analysisHistory, analysisViewIndex]);

  const handleSquareClickAnalysis = (id) => {
    if (analysisJustDraggedRef.current) return;
    const temp = new Chess(currentAnalysisFen);
    
    if (analysisSelectedSq) {
      if (id === analysisSelectedSq) {
        setAnalysisSelectedSq(null);
        setAnalysisLegalMoves([]);
        return;
      }
      if (analysisLegalMoves.includes(id)) {
        executeMoveAnalysis(analysisSelectedSq, id);
      } else {
        const piece = temp.get(id);
        if (piece) {
          setAnalysisSelectedSq(id);
          setAnalysisLegalMoves(temp.moves({ square: id, verbose: true }).map(m => m.to));
        } else {
          setAnalysisSelectedSq(null);
          setAnalysisLegalMoves([]);
        }
      }
    } else {
      const piece = temp.get(id);
      if (piece) {
        setAnalysisSelectedSq(id);
        setAnalysisLegalMoves(temp.moves({ square: id, verbose: true }).map(m => m.to));
      }
    }
  };

  const loadAnalysisFen = (newFen) => {
    try {
      const test = new Chess(newFen);
      const cleanFen = test.fen();
      setAnalysisHistory([cleanFen]);
      setAnalysisMoveHistory([]);
      setAnalysisViewIndex(0);
      setAnalysisSelectedSq(null);
      setAnalysisLegalMoves([]);
      toast.success('FEN Position Loaded! 🔬');
    } catch(e) {
      toast.error('Invalid FEN position string.');
    }
  };

  const loadAnalysisPgn = (newPgn) => {
    try {
      const test = new Chess();
      test.loadPgn(newPgn);
      
      // Rebuild history list step by step to support timeline travel
      const historyFens = [new Chess().fen()];
      const moveLabels = test.history();
      const runner = new Chess();

      moveLabels.forEach(m => {
        runner.move(m);
        historyFens.push(runner.fen());
      });

      setAnalysisHistory(historyFens);
      setAnalysisMoveHistory(moveLabels);
      setAnalysisViewIndex(historyFens.length - 1);
      setAnalysisSelectedSq(null);
      setAnalysisLegalMoves([]);
      toast.success('PGN Game Loaded! 📜');
    } catch(e) {
      toast.error('Invalid PGN notation format.');
    }
  };

  const resetAnalysisBoard = () => {
    const emptyGame = new Chess();
    setAnalysisHistory([emptyGame.fen()]);
    setAnalysisMoveHistory([]);
    setAnalysisViewIndex(0);
    setAnalysisSelectedSq(null);
    setAnalysisLegalMoves([]);
    toast.success('Analysis board reset to starting position.');
  };

  const travelToMoveIndex = (index) => {
    if (index >= 0 && index < analysisHistory.length) {
      setAnalysisViewIndex(index);
      setAnalysisSelectedSq(null);
      setAnalysisLegalMoves([]);
      playSound('move');
    }
  };

  // Keyboard navigation listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeSubTab !== 'analysis') return;
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;
      if (e.key === 'ArrowLeft') {
        travelToMoveIndex(Math.max(0, analysisViewIndex - 1));
      } else if (e.key === 'ArrowRight') {
        travelToMoveIndex(Math.min(analysisHistory.length - 1, analysisViewIndex + 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeSubTab, analysisHistory, analysisViewIndex]);

  const copyAnalysisFen = () => {
    navigator.clipboard.writeText(currentAnalysisFen);
    toast.success('FEN copied to clipboard! 📋');
  };

  const handlePlayVsBotFromAnalysis = () => {
    localStorage.setItem('arenaStartFen', currentAnalysisFen);
    toast.success('Loading position... Redirection to Arena! 🤖');
    setActiveTab('arena');
  };

  const handleEditBoardFromAnalysis = () => {
    // Sync FEN to board editor
    loadFenToVisualEditor(currentAnalysisFen);
    setActiveSubTab('editor');
    toast.success('Position loaded into Board Editor! 🛠️');
  };


  // -------------------------------------------------------------
  // STATE & INITIALIZATION FOR: BOARD EDITOR TAB
  // -------------------------------------------------------------
  const [editorSetupBoard, setEditorSetupBoard] = useState({
    a1: 'wR', b1: 'wN', c1: 'wB', d1: 'wQ', e1: 'wK', f1: 'wB', g1: 'wN', h1: 'wR',
    a2: 'wP', b2: 'wP', c2: 'wP', d2: 'wP', e2: 'wP', f2: 'wP', g2: 'wP', h2: 'wP',
    a7: 'bP', b7: 'bP', c7: 'bP', d7: 'bP', e7: 'bP', f7: 'bP', g7: 'bP', h7: 'bP',
    a8: 'bR', b8: 'bN', c8: 'bB', d8: 'bQ', e8: 'bK', f8: 'bB', g8: 'bN', h8: 'bR'
  });
  const [editorSetupTool, setEditorSetupTool] = useState(null); // 'wK', 'wQ', etc. or 'eraser'
  const [editorTurn, setEditorTurn] = useState('w');
  const [editorCastling, setEditorCastling] = useState({ K: true, Q: true, k: true, q: true });
  const [editorEnPassant, setEditorEnPassant] = useState('-');
  const [editorIsFlipped, setEditorIsFlipped] = useState(false);
  const [editorFenInput, setEditorFenInput] = useState('');

  const loadFenToVisualEditor = (fenStr) => {
    try {
      const parts = fenStr.split(' ');
      if (parts.length >= 6) {
        setEditorTurn(parts[1]);
        setEditorCastling({
          K: parts[2].includes('K'), Q: parts[2].includes('Q'),
          k: parts[2].includes('k'), q: parts[2].includes('q')
        });
        setEditorEnPassant(parts[3] || '-');
      }

      const boardObj = {};
      const rows = parts[0].split('/');
      const cols = ['a','b','c','d','e','f','g','h'];
      rows.forEach((r, rIdx) => {
        let cIdx = 0;
        for (let char of r) {
          if (!isNaN(char)) {
            cIdx += parseInt(char);
          } else {
            boardObj[`${cols[cIdx]}${8 - rIdx}`] = (char === char.toUpperCase() ? 'w' : 'b') + char.toUpperCase();
            cIdx++;
          }
        }
      });
      setEditorSetupBoard(boardObj);
    } catch(e) {}
  };

  const generateFenFromVisualEditor = () => {
    let fen = '';
    for (let r = 8; r >= 1; r--) {
      let empty = 0;
      for (let c = 0; c < 8; c++) {
        const sq = `${String.fromCharCode(97 + c)}${r}`;
        const p = editorSetupBoard[sq];
        if (p) {
          if (empty > 0) { fen += empty; empty = 0; }
          fen += p[0] === 'w' ? p[1].toUpperCase() : p[1].toLowerCase();
        } else {
          empty++;
        }
      }
      if (empty > 0) fen += empty;
      if (r > 1) fen += '/';
    }
    
    let castlingStr = '';
    if (editorCastling.K) castlingStr += 'K';
    if (editorCastling.Q) castlingStr += 'Q';
    if (editorCastling.k) castlingStr += 'k';
    if (editorCastling.q) castlingStr += 'q';
    if (castlingStr === '') castlingStr = '-';

    return `${fen} ${editorTurn} ${castlingStr} ${editorEnPassant || '-'} 0 1`;
  };

  // Sync visual layout modifications to FEN input box
  const currentEditorFen = useMemo(() => {
    return generateFenFromVisualEditor();
  }, [editorSetupBoard, editorTurn, editorCastling, editorEnPassant]);

  useEffect(() => {
    setEditorFenInput(currentEditorFen);
  }, [currentEditorFen]);

  const handleSquareClickEditor = (sq) => {
    if (!editorSetupTool) return;
    const newBoard = { ...editorSetupBoard };
    if (editorSetupTool === 'eraser') {
      delete newBoard[sq];
    } else {
      newBoard[sq] = editorSetupTool;
    }
    setEditorSetupBoard(newBoard);
    playSound('move');
  };

  const clearEditorBoard = () => {
    setEditorSetupBoard({});
    setEditorEnPassant('-');
    toast.success('Editor board cleared!');
  };

  const resetEditorToStarting = () => {
    loadFenToVisualEditor('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
    toast.success('Editor board reset to starting position.');
  };

  const handleManualEditorFenLoad = () => {
    try {
      const test = new Chess(editorFenInput);
      loadFenToVisualEditor(test.fen());
      toast.success('FEN Position loaded to Editor! 🛠️');
    } catch(e) {
      toast.error('Invalid FEN position string.');
    }
  };

  const handleAnalyzePositionFromEditor = () => {
    const finalFen = generateFenFromVisualEditor();
    const test = new Chess();
    const isValid = test.load(finalFen);
    
    if (isValid) {
      setAnalysisHistory([finalFen]);
      setAnalysisMoveHistory([]);
      setAnalysisViewIndex(0);
      setAnalysisSelectedSq(null);
      setAnalysisLegalMoves([]);
      setActiveSubTab('analysis');
      toast.success('Loaded edited setup into Analysis Board! 🔬');
    } else {
      toast.error('Illegal position setup. Check Kings and rules.');
    }
  };

  const handlePlayVsBotFromEditor = () => {
    const finalFen = generateFenFromVisualEditor();
    const test = new Chess();
    const isValid = test.load(finalFen);
    
    if (isValid) {
      localStorage.setItem('arenaStartFen', finalFen);
      toast.success('Redirection to Arena matchmaker... 🤖');
      setActiveTab('arena');
    } else {
      toast.error('Illegal position setup. Check Kings and rules.');
    }
  };

  const editorRows = editorIsFlipped ? [...baseRows].reverse() : baseRows;
  const editorCols = editorIsFlipped ? [...baseCols].reverse() : baseCols;

  // Render variables
  const analysisMovePairs = useMemo(() => {
    const list = [];
    for (let i = 0; i < analysisMoveHistory.length; i += 2) {
      list.push([analysisMoveHistory[i], analysisMoveHistory[i + 1]]);
    }
    return list;
  }, [analysisMoveHistory]);

  return (
    <div style={{ padding: '10px', width: '100%', maxWidth: '1100px', margin: '0 auto' }}>
      
      {/* 🧭 NAVIGATION TOP HEADER CONTROL PANEL */}
      <div className="glass-panel" style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          gap: '20px', 
          padding: '15px 25px', 
          marginBottom: '20px',
          border: '3px solid rgba(255, 255, 255, 0.9)',
          boxShadow: 'var(--panel-shadow)',
          background: 'var(--bg-primary)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{ fontSize: '36px', background: 'rgba(59, 130, 246, 0.1)', width: '60px', height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '15px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
            🛠️
          </div>
          <div>
            <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '22px', fontWeight: '800' }}>Chess Board Tools</h2>
            <p style={{ color: '#2563eb', margin: '2px 0 0 0', fontWeight: 'bold', fontSize: '13px' }}>
              {activeSubTab === 'analysis' ? '🔬 Analysis timeline & evaluation board' : '🎨 Place pieces, setup rules & play positions'}
            </p>
          </div>
        </div>

        {/* Tab triggers */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={() => setActiveSubTab('analysis')} 
            className="kid-btn-bubble"
            style={{
              padding: '10px 20px', borderRadius: '50px', fontWeight: '700', cursor: 'pointer', border: 'none', fontSize: '13px',
              background: activeSubTab === 'analysis' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'var(--bg-primary)',
              color: activeSubTab === 'analysis' ? '#fff' : 'var(--text-secondary)',
              boxShadow: activeSubTab === 'analysis' ? '0 4px 10px rgba(37, 99, 235, 0.35)' : 'var(--neu-shadow)'
            }}
          >
            🔬 Analysis Board
          </button>
          <button 
            onClick={() => setActiveSubTab('editor')} 
            className="kid-btn-bubble"
            style={{
              padding: '10px 20px', borderRadius: '50px', fontWeight: '700', cursor: 'pointer', border: 'none', fontSize: '13px',
              background: activeSubTab === 'editor' ? 'linear-gradient(135deg, #10b981, #059669)' : 'var(--bg-primary)',
              color: activeSubTab === 'editor' ? '#fff' : 'var(--text-secondary)',
              boxShadow: activeSubTab === 'editor' ? '0 4px 10px rgba(16, 185, 129, 0.35)' : 'var(--neu-shadow)'
            }}
          >
            🎨 Board Editor
          </button>
        </div>
      </div>

      {/* 🔬 SUB TAB: ANALYSIS BOARD */}
      {activeSubTab === 'analysis' && (
        <div style={{ display: 'flex', gap: '25px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start' }}>
          
          {/* Board column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            
            {/* Interactive analysis board */}
            <div 
              ref={analysisBoardRef}
              style={{
                width: boardWidth,
                height: boardWidth,
                position: 'relative',
                borderRadius: '6px',
                overflow: 'hidden',
                boxShadow: 'var(--panel-shadow)',
                cursor: analysisDragSq ? 'grabbing' : 'default',
                touchAction: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none'
              }}
            >
              {/* 8x8 squares */}
              {analysisRows.map((row, rIdx) => analysisCols.map((col, cIdx) => {
                const id = `${col}${row}`;
                const isDark = (rIdx + cIdx) % 2 === 1;
                const isSelected = analysisSelectedSq === id || analysisDragSq === id;
                const isLegal = analysisLegalMoves.includes(id);
                const hasPiece = !!analysisPieceMap[id];

                return (
                  <div 
                    key={id}
                    onClick={() => { if (!analysisDragSq) handleSquareClickAnalysis(id); }}
                    style={{
                      position: 'absolute',
                      left: cIdx * sqSize,
                      top: rIdx * sqSize,
                      width: sqSize,
                      height: sqSize,
                      background: isSelected ? '#f6f669' : (isDark ? darkSquareColor : lightSquareColor),
                      boxSizing: 'border-box',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    {/* Legal dot */}
                    {isLegal && !hasPiece && (
                      <div style={{ width: '32%', height: '32%', background: 'rgba(0,0,0,0.2)', borderRadius: '50%', pointerEvents: 'none' }} />
                    )}
                    {/* Capture border */}
                    {isLegal && hasPiece && (
                      <div style={{ position: 'absolute', inset: 0, border: `${Math.max(3, sqSize * 0.07)}px solid rgba(0,0,0,0.25)`, borderRadius: '50%', pointerEvents: 'none' }} />
                    )}

                    {/* Coordinates on edge squares */}
                    {cIdx === 0 && (
                      <span style={{ position: 'absolute', top: 2, left: 3, fontSize: '10px', fontWeight: 'bold', color: isDark ? lightSquareColor : darkSquareColor, opacity: 0.5, pointerEvents: 'none' }}>
                        {row}
                      </span>
                    )}
                    {rIdx === 7 && (
                      <span style={{ position: 'absolute', bottom: 1, right: 3, fontSize: '10px', fontWeight: 'bold', color: isDark ? lightSquareColor : darkSquareColor, opacity: 0.5, pointerEvents: 'none' }}>
                        {col}
                      </span>
                    )}
                  </div>
                );
              }))}

              {/* Rendered Piece Set images */}
              {Object.entries(analysisPieceMap).map(([id, piece]) => {
                const cIdx = analysisCols.indexOf(id[0]);
                const rIdx = analysisRows.indexOf(id[1]);
                if (cIdx < 0 || rIdx < 0) return null;

                const isBeingDragged = analysisDragSq === id;

                return (
                  <img 
                    key={id}
                    src={currentPieceSet[piece]}
                    alt={piece}
                    draggable={false}
                    onDragStart={e => e.preventDefault()}
                    onPointerDown={(e) => handlePointerDownAnalysis(e, id)}
                    style={{
                      position: 'absolute',
                      left: cIdx * sqSize,
                      top: rIdx * sqSize,
                      width: sqSize,
                      height: sqSize,
                      cursor: 'grab',
                      zIndex: 10,
                      opacity: isBeingDragged ? 0.3 : 1,
                      filter: analysisSelectedSq === id ? 'drop-shadow(0 0 6px rgba(255,255,100,0.9))' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.45))',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      pointerEvents: 'auto',
                      touchAction: 'none'
                    }}
                  />
                );
              })}

              {/* Drag piece clone tracking pointer */}
              {analysisDragSq && analysisDragPos && analysisPieceMap[analysisDragSq] && (
                <img 
                  src={currentPieceSet[analysisPieceMap[analysisDragSq]]}
                  alt="dragging"
                  draggable={false}
                  style={{
                    position: 'absolute',
                    left: analysisDragPos.x - analysisPointerDownOffsetRef.current.x,
                    top: analysisDragPos.y - analysisPointerDownOffsetRef.current.y,
                    width: sqSize,
                    height: sqSize,
                    pointerEvents: 'none',
                    zIndex: 9999,
                    filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))',
                    userSelect: 'none',
                    opacity: 0.95
                  }}
                />
              )}
            </div>

            {/* Board Navigation Controls */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'center' }}>
              <button onClick={() => travelToMoveIndex(0)} className="neu-btn" style={{ padding: '8px 16px', fontSize: '13px' }} title="Starting Position">⏮</button>
              <button onClick={() => travelToMoveIndex(Math.max(0, analysisViewIndex - 1))} className="neu-btn" style={{ padding: '8px 16px', fontSize: '13px' }} title="Previous Move">◀</button>
              <button onClick={() => travelToMoveIndex(Math.min(analysisHistory.length - 1, analysisViewIndex + 1))} className="neu-btn" style={{ padding: '8px 16px', fontSize: '13px' }} title="Next Move">▶</button>
              <button onClick={() => travelToMoveIndex(analysisHistory.length - 1)} className="neu-btn" style={{ padding: '8px 16px', fontSize: '13px' }} title="Latest Position">⏭</button>
              <div style={{ width: '2px', background: 'rgba(163, 177, 198, 0.4)', margin: '0 4px' }} />
              <button onClick={() => setAnalysisIsFlipped(!analysisIsFlipped)} className="neu-btn" style={{ padding: '8px 16px', fontSize: '13px' }} title="Flip Board">🔃 Flip</button>
              <button onClick={resetAnalysisBoard} className="neu-btn" style={{ padding: '8px 16px', fontSize: '13px', color: '#ef4444' }} title="Reset Board">🗑️ Reset</button>
            </div>
          </div>

          {/* Notation & Input panels */}
          <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            
            {/* Lichess Style Move History */}
            <div className="glass-panel" style={{ height: '220px', display: 'flex', flexDirection: 'column', borderRadius: '16px' }}>
              <div style={{ background: 'rgba(0,0,0,0.1)', padding: '10px 15px', borderBottom: '1px solid rgba(163, 177, 198, 0.3)', fontWeight: '800', color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>📜 Notation Timeline</span>
                <span style={{ fontSize: '11px', background: '#3b82f6', color: 'white', padding: '2px 8px', borderRadius: '10px' }}>
                  {analysisMoveHistory.length} Moves
                </span>
              </div>
              
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', alignContent: 'flex-start', flexWrap: 'wrap', gap: '6px' }}>
                {analysisMoveHistory.length === 0 ? (
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic', margin: 'auto' }}>
                    Play moves on the board to analyze... ♟️
                  </span>
                ) : (
                  analysisMovePairs.map((pair, idx) => {
                    const whiteIdx = idx * 2 + 1;
                    const blackIdx = idx * 2 + 2;
                    return (
                      <div key={idx} style={{ display: 'flex', gap: '4px', fontSize: '13px', alignItems: 'center', background: 'var(--bg-primary)', padding: '4px 8px', borderRadius: '6px', width: '100%', border: '1px solid rgba(163, 177, 198, 0.2)', boxShadow: 'var(--neu-shadow-inset)' }}>
                        <span style={{ color: '#f59e0b', fontWeight: '800', width: '25px', flexShrink: 0 }}>{idx + 1}.</span>
                        <span 
                          onClick={() => travelToMoveIndex(whiteIdx)} 
                          style={{
                            color: analysisViewIndex === whiteIdx ? 'white' : 'var(--text-primary)', 
                            fontWeight: 'bold', flex: 1, cursor: 'pointer', 
                            background: analysisViewIndex === whiteIdx ? '#3b82f6' : 'transparent', 
                            padding: '2px 6px', borderRadius: '4px', textAlign: 'center'
                          }}
                        >
                          {pair[0]}
                        </span>
                        {pair[1] ? (
                          <span 
                            onClick={() => travelToMoveIndex(blackIdx)} 
                            style={{
                              color: analysisViewIndex === blackIdx ? 'white' : 'var(--text-primary)', 
                              fontWeight: 'bold', flex: 1, cursor: 'pointer', 
                              background: analysisViewIndex === blackIdx ? '#3b82f6' : 'transparent', 
                              padding: '2px 6px', borderRadius: '4px', textAlign: 'center'
                            }}
                          >
                            {pair[1]}
                          </span>
                        ) : (
                          <span style={{ flex: 1 }} />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Actions Quick Launcher */}
            <div className="glass-panel" style={{ padding: '15px 20px', display: 'flex', gap: '10px', borderRadius: '16px' }}>
              <button onClick={handleEditBoardFromAnalysis} className="neu-btn" style={{ flex: 1, padding: '10px 0', fontSize: '13px', background: 'var(--bg-primary)' }}>
                🛠️ Edit Board Setup
              </button>
              <button onClick={handlePlayVsBotFromAnalysis} className="neu-btn-primary" style={{ flex: 1, padding: '10px 0', fontSize: '13px', boxShadow: 'none' }}>
                🤖 Play Position vs Bot
              </button>
            </div>

            {/* FEN Import/Export */}
            <div className="glass-panel" style={{ padding: '15px 20px', borderRadius: '16px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)', fontSize: '14px', fontWeight: '800' }}>🔬 FEN Position String</h4>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input 
                  type="text" 
                  value={analysisFenInput} 
                  onChange={e => setAnalysisFenInput(e.target.value)} 
                  placeholder="Paste FEN position..." 
                  className="neu-input" 
                  style={{ padding: '8px 12px', fontSize: '12px' }} 
                />
                <button onClick={() => loadAnalysisFen(analysisFenInput)} className="neu-btn" style={{ padding: '8px 15px', fontSize: '12px', whiteSpace: 'nowrap' }}>Load</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-primary)', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.4)', fontSize: '11px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {currentAnalysisFen}
                <button onClick={copyAnalysisFen} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#2563eb', fontWeight: 'bold', fontSize: '11px', marginLeft: '5px' }}>Copy</button>
              </div>
            </div>

            {/* PGN Game Import */}
            <div className="glass-panel" style={{ padding: '15px 20px', borderRadius: '16px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)', fontSize: '14px', fontWeight: '800' }}>📜 Import Chess Game (PGN)</h4>
              <textarea 
                value={analysisPgnInput} 
                onChange={e => setAnalysisPgnInput(e.target.value)} 
                placeholder="Paste PGN text here..." 
                className="neu-input" 
                rows={2} 
                style={{ padding: '10px', fontSize: '12px', resize: 'none', marginBottom: '10px' }} 
              />
              <button onClick={() => loadAnalysisPgn(analysisPgnInput)} className="neu-btn-primary" style={{ width: '100%', padding: '10px', fontSize: '13px', boxShadow: 'none' }}>
                Load Entire Game
              </button>
            </div>

          </div>

        </div>
      )}

      {/* 🛠️ SUB TAB: BOARD EDITOR */}
      {activeSubTab === 'editor' && (
        <div style={{ display: 'flex', gap: '25px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start' }}>
          
          {/* Setup board column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            
            {/* Palette selection bar */}
            <div className="glass-panel" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px', padding: '8px', borderRadius: '12px', maxWidth: boardWidth }}>
              {['wK','wQ','wR','wB','wN','wP','bK','bQ','bR','bB','bN','bP'].map(p => {
                const isSelected = editorSetupTool === p;
                return (
                  <img 
                    key={p} 
                    src={currentPieceSet[p]} 
                    alt={p} 
                    onClick={() => setEditorSetupTool(isSelected ? null : p)} 
                    style={{
                      width: '32px', 
                      height: '32px', 
                      cursor: 'pointer', 
                      background: isSelected ? '#3b82f6' : 'transparent', 
                      borderRadius: '6px', 
                      padding: '2px',
                      border: isSelected ? '2px solid white' : 'none'
                    }}
                  />
                );
              })}
              <div 
                onClick={() => setEditorSetupTool(editorSetupTool === 'eraser' ? null : 'eraser')} 
                style={{
                  width: '32px', 
                  height: '32px', 
                  cursor: 'pointer', 
                  background: editorSetupTool === 'eraser' ? '#ef4444' : 'rgba(163, 177, 198, 0.4)', 
                  borderRadius: '6px', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  fontSize: '16px',
                  color: 'white',
                  border: editorSetupTool === 'eraser' ? '2px solid white' : 'none'
                }}
                title="Eraser Tool"
              >
                🧹
              </div>
            </div>

            {/* Visual Setup Board Grid */}
            <div 
              style={{
                width: boardWidth,
                height: boardWidth,
                position: 'relative',
                borderRadius: '6px',
                overflow: 'hidden',
                boxShadow: 'var(--panel-shadow)'
              }}
            >
              {editorRows.map((row, rIdx) => editorCols.map((col, cIdx) => {
                const sq = `${col}${row}`;
                const isDark = (rIdx + cIdx) % 2 === 1;
                const pieceCode = editorSetupBoard[sq];

                return (
                  <div 
                    key={sq} 
                    onClick={() => handleSquareClickEditor(sq)}
                    style={{
                      position: 'absolute',
                      left: cIdx * sqSize,
                      top: rIdx * sqSize,
                      width: sqSize,
                      height: sqSize,
                      background: isDark ? darkSquareColor : lightSquareColor,
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      cursor: editorSetupTool ? 'crosshair' : 'default'
                    }}
                  >
                    {pieceCode && (
                      <img 
                        src={currentPieceSet[pieceCode]} 
                        alt="piece" 
                        style={{ width: '85%', height: '85%', pointerEvents: 'none' }} 
                      />
                    )}

                    {/* Coordinates */}
                    {cIdx === 0 && (
                      <span style={{ position: 'absolute', top: 2, left: 3, fontSize: '10px', fontWeight: 'bold', color: isDark ? lightSquareColor : darkSquareColor, opacity: 0.5, pointerEvents: 'none' }}>
                        {row}
                      </span>
                    )}
                    {rIdx === 7 && (
                      <span style={{ position: 'absolute', bottom: 1, right: 3, fontSize: '10px', fontWeight: 'bold', color: isDark ? lightSquareColor : darkSquareColor, opacity: 0.5, pointerEvents: 'none' }}>
                        {col}
                      </span>
                    )}
                  </div>
                );
              }))}
            </div>

            {/* Quick Actions Board Controls */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'center' }}>
              <button onClick={resetEditorToStarting} className="neu-btn" style={{ padding: '8px 16px', fontSize: '13px' }}>Standard Start</button>
              <button onClick={clearEditorBoard} className="neu-btn" style={{ padding: '8px 16px', fontSize: '13px', color: '#ef4444' }}>Clear Board</button>
              <button onClick={() => setEditorIsFlipped(!editorIsFlipped)} className="neu-btn" style={{ padding: '8px 16px', fontSize: '13px' }}>🔃 Flip Board</button>
            </div>
          </div>

          {/* Rules and Setup Configuration sidebar */}
          <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            
            {/* Rules panel */}
            <div className="glass-panel" style={{ padding: '20px', borderRadius: '16px' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#f59e0b', fontSize: '16px', fontWeight: '800' }}>⚖️ Setup Rules</h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {/* Side to Move */}
                <div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800', display: 'block', marginBottom: '8px' }}>Turn to Move:</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                      onClick={() => setEditorTurn('w')} 
                      className="kid-btn-bubble"
                      style={{
                        padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', border: 'none', fontSize: '13px',
                        background: editorTurn === 'w' ? '#3b82f6' : 'var(--bg-primary)',
                        color: editorTurn === 'w' ? 'white' : 'var(--text-primary)',
                        boxShadow: editorTurn === 'w' ? '0 4px 10px rgba(37, 99, 235, 0.3)' : 'var(--neu-shadow)',
                        flex: 1
                      }}
                    >
                      ⬜ White to move
                    </button>
                    <button 
                      onClick={() => setEditorTurn('b')} 
                      className="kid-btn-bubble"
                      style={{
                        padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', border: 'none', fontSize: '13px',
                        background: editorTurn === 'b' ? '#111827' : 'var(--bg-primary)',
                        color: editorTurn === 'b' ? 'white' : 'var(--text-primary)',
                        boxShadow: editorTurn === 'b' ? '0 4px 10px rgba(0, 0, 0, 0.3)' : 'var(--neu-shadow)',
                        flex: 1
                      }}
                    >
                      ⬛ Black to move
                    </button>
                  </div>
                </div>

                {/* Castling rights */}
                <div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800', display: 'block', marginBottom: '8px' }}>Castling Available:</span>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '13px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)', fontWeight: '600' }}>
                      <input type="checkbox" checked={editorCastling.K} onChange={e => setEditorCastling({ ...editorCastling, K: e.target.checked })} />
                      White O-O (K)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)', fontWeight: '600' }}>
                      <input type="checkbox" checked={editorCastling.Q} onChange={e => setEditorCastling({ ...editorCastling, Q: e.target.checked })} />
                      White O-O-O (Q)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)', fontWeight: '600' }}>
                      <input type="checkbox" checked={editorCastling.k} onChange={e => setEditorCastling({ ...editorCastling, k: e.target.checked })} />
                      Black O-O (k)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)', fontWeight: '600' }}>
                      <input type="checkbox" checked={editorCastling.q} onChange={e => setEditorCastling({ ...editorCastling, q: e.target.checked })} />
                      Black O-O-O (q)
                    </label>
                  </div>
                </div>

                {/* En passant target */}
                <div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800', display: 'block', marginBottom: '6px' }}>En Passant Square (or -):</span>
                  <input 
                    type="text" 
                    value={editorEnPassant} 
                    onChange={e => setEditorEnPassant(e.target.value.toLowerCase().trim() || '-')} 
                    maxLength={2}
                    className="neu-input"
                    style={{ padding: '8px 12px', width: '100px', textAlign: 'center', fontSize: '13px', fontWeight: 'bold' }}
                  />
                </div>
              </div>

            </div>

            {/* Launchers */}
            <div className="glass-panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px', borderRadius: '16px' }}>
              <button onClick={handleAnalyzePositionFromEditor} className="neu-btn-primary" style={{ padding: '12px', fontSize: '14px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>
                🔬 Load into Analysis Board
              </button>
              <button onClick={handlePlayVsBotFromEditor} className="neu-btn-primary" style={{ padding: '12px', fontSize: '14px', background: 'linear-gradient(135deg, #10b981, #059669)', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.3)' }}>
                🤖 Play Position vs Bot
              </button>
            </div>

            {/* FEN String Synchronizer box */}
            <div className="glass-panel" style={{ padding: '20px', borderRadius: '16px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)', fontSize: '14px', fontWeight: '800' }}>🔬 Setup FEN Position</h4>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  type="text" 
                  value={editorFenInput} 
                  onChange={e => setEditorFenInput(e.target.value)} 
                  placeholder="Paste FEN..." 
                  className="neu-input" 
                  style={{ padding: '8px 12px', fontSize: '11px', fontFamily: 'monospace' }} 
                />
                <button onClick={handleManualEditorFenLoad} className="neu-btn" style={{ padding: '8px 15px', fontSize: '12px' }}>Load</button>
              </div>
            </div>

          </div>

        </div>
      )}

    </div>
  );
}
